/**
 * Production-grade automatic retry utility with exponential backoff + jitter.
 *
 * Usage:
 *   const { data, error } = await withRetry(() => supabase.from('table').select('*'));
 *   const result = await withRetry(fetchFn, { maxAttempts: 5, baseDelayMs: 500 });
 */

// ─── Default Config ────────────────────────────────────────────────────────────
const DEFAULT_OPTIONS = {
  maxAttempts: 3,          // Total attempts (1 initial + N-1 retries)
  baseDelayMs: 300,        // Base delay before first retry (ms)
  maxDelayMs: 10000,       // Cap on delay between retries (ms)
  backoffFactor: 2,        // Exponential multiplier per attempt
  jitter: true,            // Add random jitter to avoid thundering herd
  retryOn: null,           // Optional fn(error, attempt) => bool — custom retry predicate
  onRetry: null,           // Optional fn(error, attempt, delayMs) — called before each retry
};

// ─── Retryable Error Detection ─────────────────────────────────────────────────
const isRetryableError = (error) => {
  if (!error) return false;

  // Network / fetch errors
  if (error instanceof TypeError && error?.message?.includes('fetch')) return true;
  if (error?.name === 'AbortError') return false; // Don't retry aborted requests

  // Supabase / PostgREST error codes
  const code = error?.code || error?.status || error?.statusCode;
  const msg  = (error?.message || '')?.toLowerCase();

  // Transient HTTP codes worth retrying
  const retryableCodes = [408, 429, 500, 502, 503, 504];
  if (retryableCodes?.includes(Number(code))) return true;

  // Supabase-specific transient messages
  if (msg?.includes('connection') || msg?.includes('timeout') || msg?.includes('network') ||
      msg?.includes('econnreset') || msg?.includes('enotfound') || msg?.includes('socket')) {
    return true;
  }

  // Rate limit
  if (msg?.includes('rate limit') || msg?.includes('too many requests')) return true;

  return false;
};

// ─── Delay Helper ──────────────────────────────────────────────────────────────
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const calcDelay = (attempt, options) => {
  const { baseDelayMs, maxDelayMs, backoffFactor, jitter } = options;
  let delay = baseDelayMs * Math.pow(backoffFactor, attempt - 1);
  if (jitter) {
    // Full jitter: random between [0, delay]
    delay = Math.random() * delay;
  }
  return Math.min(delay, maxDelayMs);
};

// ─── Core Retry Wrapper ────────────────────────────────────────────────────────
/**
 * Wraps an async function with automatic retry logic.
 *
 * @param {Function} fn - Async function to execute. Should return a value or throw.
 * @param {Object}   opts - Optional configuration overrides.
 * @returns {Promise<any>} - Resolves with fn's return value, or throws after all attempts exhausted.
 */
export const withRetry = async (fn, opts = {}) => {
  const options = { ...DEFAULT_OPTIONS, ...opts };
  const { maxAttempts, retryOn, onRetry } = options;

  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await fn();

      // Handle Supabase-style { data, error } responses
      if (result && typeof result === 'object' && 'error' in result && result?.error) {
        const supabaseError = result?.error;
        const shouldRetry = retryOn
          ? retryOn(supabaseError, attempt)
          : isRetryableError(supabaseError);

        if (shouldRetry && attempt < maxAttempts) {
          const delayMs = calcDelay(attempt, options);
          lastError = supabaseError;
          onRetry?.(supabaseError, attempt, delayMs);
          await sleep(delayMs);
          continue;
        }
        // Non-retryable or exhausted — return the Supabase result as-is
        return result;
      }

      return result;
    } catch (error) {
      lastError = error;
      const shouldRetry = retryOn
        ? retryOn(error, attempt)
        : isRetryableError(error);

      if (shouldRetry && attempt < maxAttempts) {
        const delayMs = calcDelay(attempt, options);
        onRetry?.(error, attempt, delayMs);
        await sleep(delayMs);
        continue;
      }

      throw error;
    }
  }

  throw lastError;
};

// ─── Supabase-Specific Wrapper ─────────────────────────────────────────────────
/**
 * Wraps a Supabase query builder call with retry logic.
 * Returns { data, error } — same shape as Supabase responses.
 *
 * @param {Function} queryFn - () => supabase.from(...).select(...)
 * @param {Object}   opts    - Retry options
 */
export const supabaseWithRetry = async (queryFn, opts = {}) => {
  try {
    return await withRetry(queryFn, {
      maxAttempts: 3,
      baseDelayMs: 400,
      ...opts,
    });
  } catch (error) {
    return { data: null, error };
  }
};

// ─── Retry State Hook Helper ───────────────────────────────────────────────────
/**
 * Creates a retry-aware fetch function that tracks attempt count.
 * Useful for UI feedback ("Retrying… attempt 2/3").
 *
 * @param {Function} fn        - Async function to execute
 * @param {Function} onAttempt - (attempt, maxAttempts) => void — called on each retry
 * @param {Object}   opts      - Retry options
 */
export const withRetryAndFeedback = async (fn, onAttempt, opts = {}) => {
  const options = {
    ...DEFAULT_OPTIONS,
    ...opts,
    onRetry: (error, attempt, delayMs) => {
      onAttempt?.(attempt + 1, opts?.maxAttempts || DEFAULT_OPTIONS?.maxAttempts);
      opts?.onRetry?.(error, attempt, delayMs);
    },
  };
  return withRetry(fn, options);
};

// ─── Batch Retry ──────────────────────────────────────────────────────────────
/**
 * Runs multiple async functions in parallel, each with retry logic.
 * Returns array of { result, error } for each.
 *
 * @param {Array<Function>} fns  - Array of async functions
 * @param {Object}          opts - Retry options applied to each
 */
export const batchWithRetry = async (fns, opts = {}) => {
  return Promise.all(
    fns?.map(async (fn) => {
      try {
        const result = await withRetry(fn, opts);
        return { result, error: null };
      } catch (error) {
        return { result: null, error };
      }
    })
  );
};

export default withRetry;
